import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import heroImage from "@/assets/hero-travel.jpg?format=webp";

const HeroSection = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [destination, setDestination] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Please fill in your name and phone number");
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-lead-notification", {
        body: { name: name.trim(), phone: phone.trim(), email: email.trim(), destination: destination.trim() },
        headers: {
          Authorization: `Bearer ${publishableKey}`,
        },
      });

      if (error) throw error;
      toast.success("Thank you! We'll get back to you shortly.");
      setName("");
      setPhone("");
      setEmail("");
      setDestination("");
    } catch (err) {
      console.error("Lead submit error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section ref={heroRef} className="relative min-h-screen w-full overflow-hidden">
      <motion.div className="absolute inset-[-10%]" style={{ y: heroImgY, scale: heroScale }}>
        <img
          src={heroImage}
          alt="Kerala backwaters at sunset"
          className="h-full w-full object-cover brightness-110 saturate-[1.1]"
          loading="eager"
          fetchPriority="high"
        />
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-b from-deep-earth/25 via-deep-earth/10 to-deep-earth/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-deep-earth/25 via-transparent to-transparent" />

      <motion.div
        className="relative z-10 flex h-full min-h-screen items-center px-4 lg:px-8"
      >
        <div className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-32">
          {/* Left - Text */}
          <div className="text-center lg:text-left">
            <motion.p
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-body text-sm md:text-base tracking-[0.3em] uppercase text-soft-cream/80 mb-4"
            >
              Slow Travel · Wellness · Healing
            </motion.p>

            <motion.h1
              initial={{ y: 30 }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="font-display text-5xl md:text-7xl lg:text-8xl font-light text-soft-cream leading-tight"
            >
              Journey Within,
              <br />
              <span className="italic font-light">Travel Without</span>
            </motion.h1>

            <motion.p
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="font-body text-base md:text-lg text-soft-cream/70 mt-6 max-w-xl mx-auto lg:mx-0"
            >
              Curated wellness journeys across India's most transformative destinations
            </motion.p>

            <motion.a
              href="#wellness"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="mt-8 inline-flex flex-col items-center gap-2 text-soft-cream/60 hover:text-soft-cream transition-colors"
            >
              <span className="text-xs tracking-[0.2em] uppercase font-body">Explore</span>
              <ChevronDown className="animate-bounce" size={20} />
            </motion.a>
          </div>

          {/* Right - Lead Form */}
          <motion.div
            initial={{ x: 40 }}
            animate={{ x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex justify-center lg:justify-end"
          >
            <form
              onSubmit={handleSubmit}
              className="w-full max-w-sm bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 space-y-4"
            >
              <h3 className="font-display text-2xl text-soft-cream font-light text-center mb-2">
                Plan Your Journey
              </h3>
              <p className="font-body text-xs text-soft-cream/60 text-center tracking-widest uppercase mb-4">
                Free Consultation
              </p>
              <input type="text" placeholder="Your Name *" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} className="w-full px-4 py-3 text-sm font-body rounded-lg bg-white/10 border border-white/20 text-soft-cream placeholder:text-soft-cream/40 focus:outline-none focus:ring-1 focus:ring-soft-cream/50" />
              <input type="tel" placeholder="Phone Number *" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={15} className="w-full px-4 py-3 text-sm font-body rounded-lg bg-white/10 border border-white/20 text-soft-cream placeholder:text-soft-cream/40 focus:outline-none focus:ring-1 focus:ring-soft-cream/50" />
              <input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} className="w-full px-4 py-3 text-sm font-body rounded-lg bg-white/10 border border-white/20 text-soft-cream placeholder:text-soft-cream/40 focus:outline-none focus:ring-1 focus:ring-soft-cream/50" />
              <input type="text" placeholder="Preferred Destination" value={destination} onChange={(e) => setDestination(e.target.value)} maxLength={100} className="w-full px-4 py-3 text-sm font-body rounded-lg bg-white/10 border border-white/20 text-soft-cream placeholder:text-soft-cream/40 focus:outline-none focus:ring-1 focus:ring-soft-cream/50" />
              <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-body font-semibold tracking-wider uppercase rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
                <Send size={14} />
                {submitting ? "Sending..." : "Get Free Quote"}
              </button>
            </form>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
